import React from 'react';
import { Button, Card, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, SplitCellsOutlined } from '@ant-design/icons';
import type { DailySpinBandName, DailySpinOdds, DailySpinSegment } from '@/types';
import {
  BAND_LABELS,
  DAILY_CARD_RARITIES,
  DAILY_SPIN_BAND_NAMES,
  DAILY_SPIN_SEGMENTS,
  DIVISIONS,
  DIVISION_MAX,
  RARITY_LABELS,
  SEGMENT_LABELS,
  TAB_PREFIXES,
  errorsUnder,
  guaranteedOdds,
  isHundred,
  oddsSum,
  removeRange,
  respinOdds,
  setRangeEnd,
  spinBandFor,
  splitRange,
  wireProbability,
} from './dailyEngagementConfig';
import { useDraft } from './draftContext';
import { ErrorList, LabeledNumber, NumberInput } from './fields';

const { Text, Paragraph } = Typography;

const DIVISION_KEYS = { from: 'fromDivision', to: 'toDivision' } as const;

const BAND_COLORS: Record<DailySpinBandName, string> = {
  low: 'green',
  mid: 'blue',
  high: 'purple',
};

/** A percent to 3 decimals, or '?' when an input is empty. */
const pct = (n: number) => (Number.isFinite(n) ? `${Math.round(n * 1000) / 1000}` : '?');

const SumTag: React.FC<{ sum: number }> = ({ sum }) => (
  <Tag color={isHundred(sum) ? 'green' : 'red'}>
    Sum {pct(sum)}%{isHundred(sum) ? '' : ' (must be 100)'}
  </Tag>
);

/** Spin wheel: odds by division band, the RV re-spin split and the guarantee after misses. */
export const SpinTab: React.FC = () => {
  const { draft, editable, errors, update } = useDraft();
  const spin = draft.spin;
  const splitSum = oddsSum(spin.respinSplit, DAILY_CARD_RARITIES);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <ErrorList prefixes={TAB_PREFIXES.spin} />

      <Card size="small" title="Re-spin and guarantee">
        <Paragraph type="secondary">
          A card chance day gives one spin. After a No Card result the player can watch a rewarded
          video for one re-spin: No Card is halved and the freed half goes to the card segments by
          the shares below. The guarantee counts Card Chance days in a row whose first spin landed
          No Card: a card on the first spin resets the count, and the re-spin never changes it,
          whatever it lands on. Once the count reaches the guarantee, the next first spin is a
          guaranteed card, drawn from the band&apos;s own odds with No Card removed (it can&apos;t
          land No Card, so it is never re-spun).
        </Paragraph>
        <Space wrap size="large" align="start">
          <LabeledNumber
            label="Guaranteed card after"
            path="spin.guaranteeAfterMisses"
            value={spin.guaranteeAfterMisses}
            min={1}
            addonAfter="No Card days in a row"
            help="Whole number, at least 1."
            onChange={(v) =>
              update((d) => {
                d.spin.guaranteeAfterMisses = v;
              })
            }
          />
          <Space direction="vertical" size={2}>
            <Text strong>Re-spin split of the freed No Card share</Text>
            <Space wrap>
              {DAILY_CARD_RARITIES.map((r) => (
                <Space key={r} direction="vertical" size={0}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {RARITY_LABELS[r]}
                  </Text>
                  <NumberInput
                    path={`spin.respinSplit.${r}`}
                    errorPrefix="spin.respinSplit"
                    value={spin.respinSplit[r]}
                    min={0}
                    max={100}
                    integer={false}
                    addonAfter="%"
                    width={110}
                    onChange={(v) =>
                      update((d) => {
                        d.spin.respinSplit[r] = v;
                      })
                    }
                  />
                </Space>
              ))}
            </Space>
            <SumTag sum={splitSum} />
          </Space>
        </Space>
      </Card>

      <Card size="small" title="Bands by division">
        <Paragraph type="secondary">
          Bands must cover divisions 1 to {DIVISION_MAX} without gaps; changing a band&apos;s last
          division moves the next band&apos;s first one. Odds are percentages (decimals allowed) and
          must sum to 100, with at least one card segment above 0. Re-spin and guaranteed columns
          are worked out here the same way the server does; the client receives them as
          probabilities rounded to 9 decimals.
        </Paragraph>
        <Space wrap size={4} style={{ marginBottom: 16 }}>
          <Text type="secondary">Division → band:</Text>
          {DIVISIONS.map((div) => {
            const band = spinBandFor(draft, div);
            return (
              <Tag key={div} color={band ? BAND_COLORS[band.band] : undefined}>
                {div}: {band ? (BAND_LABELS[band.band] ?? band.band) : '?'}
              </Tag>
            );
          })}
        </Space>

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {spin.bands.map((band, i) => {
            const at = `spin.bands[${i}]`;
            const sum = oddsSum(band.odds);
            const respin = respinOdds(spin.respinSplit, band.odds);
            const guaranteed = guaranteedOdds(band.odds);
            const bandErrors = errorsUnder(errors, at);
            const rows = DAILY_SPIN_SEGMENTS.map((segment) => ({ segment }));
            const derivedCell = (odds: DailySpinOdds, segment: DailySpinSegment) => (
              <Tooltip title={`Sent as ${wireProbability(odds[segment])}`}>
                <span>{pct(odds[segment])}</span>
              </Tooltip>
            );
            return (
              <Card
                key={i}
                size="small"
                type="inner"
                title={
                  <Space wrap>
                    <Select<DailySpinBandName>
                      value={band.band}
                      disabled={!editable}
                      style={{ width: 100 }}
                      status={errorsUnder(errors, `${at}.band`).length ? 'error' : undefined}
                      onChange={(name) =>
                        update((d) => {
                          d.spin.bands[i].band = name;
                        })
                      }
                      options={DAILY_SPIN_BAND_NAMES.map((n) => ({
                        value: n,
                        label: BAND_LABELS[n],
                      }))}
                    />
                    <Text>Divisions</Text>
                    <Text
                      strong
                      type={errorsUnder(errors, `${at}.fromDivision`).length ? 'danger' : undefined}
                    >
                      {band.fromDivision}
                    </Text>
                    <Text>to</Text>
                    <NumberInput
                      path={`${at}.toDivision`}
                      value={band.toDivision}
                      min={1}
                      max={DIVISION_MAX}
                      width={80}
                      onChange={(v) =>
                        update((d) => {
                          d.spin.bands = setRangeEnd(d.spin.bands, i, v, DIVISION_KEYS);
                        })
                      }
                    />
                  </Space>
                }
                extra={
                  <Space size={4}>
                    <Tooltip title="Split this band in two (the copy takes the upper divisions)">
                      <Button
                        size="small"
                        icon={<SplitCellsOutlined />}
                        disabled={!editable || !(band.toDivision > band.fromDivision)}
                        onClick={() =>
                          update((d) => {
                            d.spin.bands = splitRange(d.spin.bands, i, DIVISION_KEYS);
                          })
                        }
                      />
                    </Tooltip>
                    <Tooltip title="Remove this band (its neighbour takes over its divisions)">
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={!editable || spin.bands.length <= 1}
                        onClick={() =>
                          update((d) => {
                            d.spin.bands = removeRange(d.spin.bands, i, DIVISION_KEYS);
                          })
                        }
                      />
                    </Tooltip>
                  </Space>
                }
              >
                <Table
                  size="small"
                  pagination={false}
                  rowKey="segment"
                  dataSource={rows}
                  columns={[
                    {
                      title: 'Segment',
                      render: (_v, { segment }) => SEGMENT_LABELS[segment],
                    },
                    {
                      title: 'Spin odds %',
                      render: (_v, { segment }) => (
                        <NumberInput
                          path={`${at}.odds`}
                          value={band.odds[segment]}
                          min={0}
                          max={100}
                          integer={false}
                          width={100}
                          onChange={(v) =>
                            update((d) => {
                              d.spin.bands[i].odds[segment] = v;
                            })
                          }
                        />
                      ),
                    },
                    {
                      title: 'Re-spin % (read-only)',
                      render: (_v, { segment }) => derivedCell(respin, segment),
                    },
                    {
                      title: `Guaranteed % (after ${pct(spin.guaranteeAfterMisses)} No Card days)`,
                      render: (_v, { segment }) => derivedCell(guaranteed, segment),
                    },
                  ]}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <Text strong>Sum</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <SumTag sum={sum} />
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>{pct(oddsSum(respin))}</Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>{pct(oddsSum(guaranteed))}</Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
                {bandErrors.length > 0 && (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {bandErrors.join('; ')}
                  </Text>
                )}
              </Card>
            );
          })}
        </Space>
      </Card>
    </Space>
  );
};
